/** Create the selected products' mappings under a policy, in bulk.
 *
 *  The step ops does by hand today, one row at a time in the drawer — which is
 *  how ~2 900 production tours ended up carrying four different spellings of
 *  the same typed prefix.
 *
 *  It always dry-runs first. A mapping is the thing booking matching depends
 *  on, so the operator sees exactly which IDs would be created, which products
 *  are already mapped some other way, and what would be skipped, before
 *  anything is written.
 */
import {
  Alert,
  AlertTitle,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type {
  ApplyMappingsResult,
  MappingPolicy,
  ProductListItem,
  ProductMapping,
} from '@/api/sync';
import * as syncApi from '@/api/sync';
import { useSnackbar } from '@/components/Snackbar';

import { policyStatus } from './mappingPolicy';

interface Props {
  open: boolean;
  onClose: () => void;
  onApplied: () => void;
  tourOperatorId: number;
  integration: string;
  account: string | null;
  policy: MappingPolicy | undefined;
  /** The products the operator ticked, in table order. */
  products: ProductListItem[];
  mappings: Record<string, ProductMapping[]>;
  /** Which tour each product should map to. A product with no tour cannot be
   *  mapped — the mapping's whole purpose is to name one. */
  tourFor: (productId: string) => number | undefined;
}

type Conflict = 'skip' | 'parallel' | 'rewire';
type RewireOld = 'park' | 'delete';

export function ApplyMappingsDialog({
  open,
  onClose,
  onApplied,
  tourOperatorId,
  integration,
  account,
  policy,
  products,
  mappings,
  tourFor,
}: Props) {
  const { show } = useSnackbar();
  const [onConflict, setOnConflict] = useState<Conflict>('skip');
  const [rewireOld, setRewireOld] = useState<RewireOld>('park');
  const [preview, setPreview] = useState<ApplyMappingsResult | null>(null);

  const items = products
    .map((p) => ({ product_id: p.id ?? '', tour_id: tourFor(p.id ?? '') }))
    .filter((i): i is { product_id: string; tour_id: number } =>
      Boolean(i.product_id && i.tour_id),
    );

  const withoutTour = products.filter((p) => p.id && !tourFor(p.id));
  const conflicted = products.filter(
    (p) => policyStatus(policy, p, p.id ? mappings[p.id] : undefined).conflicting,
  );

  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      syncApi.applyMappings({
        tour_operator_id: tourOperatorId,
        integration,
        account,
        policy: policy?.id ?? '',
        items,
        on_conflict: onConflict,
        rewire_old: rewireOld,
        dry_run: dryRun,
      }),
    onError: (e: Error) => show(e.message || 'Could not apply mappings', 'error'),
  });

  // Re-preview whenever the inputs change: a stale preview is worse than none,
  // because it is the thing the operator is about to approve.
  useEffect(() => {
    if (!open || !policy || items.length === 0) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    run
      .mutateAsync(true)
      .then((r) => {
        if (!cancelled) setPreview(r);
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, policy?.id, onConflict, rewireOld, products.length]);

  const apply = async () => {
    const r = await run.mutateAsync(false);
    show(
      `Created ${r.created.length} mapping${r.created.length === 1 ? '' : 's'}` +
        (r.retired.length
          ? `, ${rewireOld === 'delete' ? 'deleted' : 'parked'} ${r.retired.length}`
          : '') +
        (r.skipped.length ? `, skipped ${r.skipped.length}` : '') +
        (r.errors.length ? `, ${r.errors.length} failed` : ''),
      r.errors.length ? 'warning' : 'success',
    );
    onApplied();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Map {items.length} product{items.length === 1 ? '' : 's'}
        {policy ? ` as ${policy.label}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {policy && (
            <Typography variant="body2" color="text.secondary">
              Each product gets an external ID of the form <code>{policy.form}</code>, pointing at
              the tour it is matched to. Booking matching uses that ID from then on.
            </Typography>
          )}

          {withoutTour.length > 0 && (
            <Alert severity="warning">
              <AlertTitle>
                {withoutTour.length} selected product{withoutTour.length === 1 ? ' has' : 's have'}{' '}
                no tour
              </AlertTitle>
              A mapping names the tour a booking should land on, so these are left out. Pick a tour
              in the Mappings drawer first.
            </Alert>
          )}

          {conflicted.length > 0 && (
            <FormControl>
              <FormLabel>
                {conflicted.length} product{conflicted.length === 1 ? ' is' : 's are'} already
                mapped under another policy
              </FormLabel>
              <RadioGroup
                value={onConflict}
                onChange={(e) => setOnConflict(e.target.value as Conflict)}
              >
                <FormControlLabel
                  value="skip"
                  control={<Radio size="small" />}
                  label="Skip them — leave the existing mapping as the only one"
                />
                <FormControlLabel
                  value="parallel"
                  control={<Radio size="small" />}
                  label="Create alongside — both IDs resolve to the tour"
                />
                <FormControlLabel
                  value="rewire"
                  control={<Radio size="small" />}
                  label="Rewire — create the new mapping and retire the old one"
                />
              </RadioGroup>

              {onConflict === 'rewire' && (
                <FormControl sx={{ mt: 1, ml: 3 }}>
                  <FormLabel>What happens to the old mapping</FormLabel>
                  <RadioGroup
                    value={rewireOld}
                    onChange={(e) => setRewireOld(e.target.value as RewireOld)}
                  >
                    <FormControlLabel
                      value="park"
                      control={<Radio size="small" />}
                      label="Park it — stops matching, stays visible, can be switched back"
                    />
                    <FormControlLabel
                      value="delete"
                      control={<Radio size="small" />}
                      label="Delete it — permanent"
                    />
                  </RadioGroup>
                </FormControl>
              )}

              <Typography variant="caption" color="text.secondary">
                A tour&apos;s own external ID is never changed here — it is the tour&apos;s identity
                rather than an extra name for it, and the importer writes it back. Those are
                reported instead of rewired.
              </Typography>
            </FormControl>
          )}

          {preview && (
            <>
              <Stack direction="row" spacing={1}>
                <Chip size="small" color="success" label={`${preview.created.length} to create`} />
                {preview.skipped.length > 0 && (
                  <Chip size="small" label={`${preview.skipped.length} skipped`} />
                )}
                {preview.conflicts.length > 0 && (
                  <Chip
                    size="small"
                    color="warning"
                    label={`${preview.conflicts.length} alongside existing`}
                  />
                )}
                {preview.retired.length > 0 && (
                  <Chip
                    size="small"
                    color="warning"
                    label={`${preview.retired.length} to ${rewireOld}`}
                  />
                )}
                {preview.errors.length > 0 && (
                  <Chip size="small" color="error" label={`${preview.errors.length} cannot`} />
                )}
              </Stack>

              {preview.created.length > 0 && (
                <TableContainer sx={{ maxHeight: 280, border: 1, borderColor: 'divider' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell>External ID to create</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.created.slice(0, 200).map((c, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <code>{c.product_id}</code>
                          </TableCell>
                          <TableCell>
                            <code>{c.custom_id}</code>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {preview.errors.length > 0 && (
                <Alert severity="error">
                  <AlertTitle>Cannot be mapped</AlertTitle>
                  <Stack component="ul" sx={{ pl: 2, m: 0 }}>
                    {preview.errors.slice(0, 10).map((e, i) => (
                      <li key={i}>
                        <code>{e.product_id}</code> — {e.detail}
                      </li>
                    ))}
                  </Stack>
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!policy || run.isPending || !preview || preview.created.length === 0}
          onClick={apply}
        >
          Create {preview?.created.length ?? 0} mapping
          {preview?.created.length === 1 ? '' : 's'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
